import type { RagAlternativeSource, RagImageItem } from "../types/ragChatTypes";

/** Normalize PDF identifiers for set comparisons (case/path agnostic). */
export function normalizePdfKey(name: string): string {
  return (name || "").trim().toLowerCase().replace(/\.pdf$/i, "");
}

/**
 * Human-readable video filename from a worker path or signed playback URL.
 * Strips query params (e.g. ?token=) and URL-decodes %20 etc.
 */
export function videoDisplayName(pathOrUrl: string, fallback = "Video"): string {
  const withoutQuery = (pathOrUrl || "").split("?")[0] ?? "";
  const segment =
    withoutQuery.replace(/\\/g, "/").split("/").pop()?.trim() || fallback;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export type SourceMediaKind = "pdf" | "video" | "other";

const VIDEO_EXTENSIONS = new Set([
  ".mp4",
  ".mov",
  ".avi",
  ".mkv",
  ".webm",
  ".m4v",
]);

function fileExtension(fileName: string): string {
  const base =
    (fileName || "").split("?")[0].replace(/\\/g, "/").split("/").pop() || "";
  const dot = base.lastIndexOf(".");
  return dot >= 0 ? base.slice(dot).toLowerCase() : "";
}

/**
 * Classify a source/document label for icon + click behavior.
 * Prefers backend `type` when present; otherwise uses filename extension.
 */
export function sourceMediaKind(
  fileName: string,
  type?: string | null
): SourceMediaKind {
  const t = (type || "").trim().toLowerCase();
  if (t === "video") return "video";
  if (t === "pdf" || t === "text") {
    const ext = fileExtension(fileName);
    if (VIDEO_EXTENSIONS.has(ext)) return "video";
    return "pdf";
  }

  const ext = fileExtension(fileName);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (ext === ".pdf") return "pdf";
  return "other";
}

/**
 * PDFs listed in alternativePdfs that are not already covered by alternativeSources.
 * When every alternative PDF has a matching source card, returns [] so "Also available in"
 * can be hidden without hardcoding document names.
 */
export function alternativePdfsNotInSources(
  pdfs: string[] | undefined,
  sources: RagAlternativeSource[] | undefined
): string[] {
  const sourceKeys = new Set(
    (sources ?? []).flatMap((s) =>
      [s.pdf_name, s.source_label].filter(Boolean).map((n) => normalizePdfKey(String(n)))
    )
  );
  return (pdfs ?? []).filter((pdf) => pdf.trim() && !sourceKeys.has(normalizePdfKey(pdf)));
}

export interface ParsedMarkdownTable {
  headers: string[];
  rows: string[][];
}

/** Converts a GFM markdown table string into headers + rows, or null if invalid. */
export function parseMarkdownTable(md: string): ParsedMarkdownTable | null {
  const lines = md
    .trim()
    .split("\n")
    .filter((l) => l.trim());
  if (lines.length < 3) return null;

  const parseRow = (line: string) =>
    line
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  const headers = parseRow(lines[0]);
  const isSep = lines[1].replace(/[\s|:-]/g, "").length === 0;
  if (!isSep) return null;

  const rows = lines.slice(2).map(parseRow);
  return { headers, rows };
}

export interface TextSegment {
  text: string;
  page: string | null;
}

/** Splits on [p.N] citation markers. */
export function parseSegments(text: string): TextSegment[] {
  const parts = text.split(/(\[p\.(\d+)\])/);
  const segments: TextSegment[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (/^\[p\.\d+\]$/.test(parts[i])) {
      const page = parts[i + 1];
      if (segments.length > 0) {
        segments[segments.length - 1].page = page ?? null;
      } else {
        segments.push({ text: "", page: page ?? null });
      }
      i++;
    } else if (parts[i]) {
      segments.push({ text: parts[i], page: null });
    }
  }
  return segments;
}

export interface ParsedStep {
  num: string | null;
  text: string;
}

const STEP_LINE = /^(\d+)[\.\)]\s+(.*)/i;
const STEP_WORD = /^step\s+(\d+)[\.:]\s*(.*)/i;

export function parseNumberedSteps(text: string): ParsedStep[] | null {
  const lines = text.split("\n");
  const steps: ParsedStep[] = [];
  let current: ParsedStep | null = null;

  for (const line of lines) {
    const m1 = line.match(STEP_LINE);
    const m2 = !m1 ? line.match(STEP_WORD) : null;
    const m = m1 || m2;
    if (m) {
      if (current) steps.push(current);
      current = { num: m[1], text: m[2] ?? "" };
    } else if (current) {
      current.text += line.trim() ? ` ${line.trim()}` : "";
    } else if (line.trim()) {
      steps.push({ num: null, text: line });
    }
  }
  if (current) steps.push(current);

  const numbered = steps.filter((s) => s.num);
  if (numbered.length === 0) return null;
  return steps;
}

export interface QueuedInlineImage {
  img: RagImageItem;
  page: string;
}

/** Page-ordered queue for [img:N] tags and proportional fallback. */
export function buildOrderedImageQueue(
  inlineImages: Record<string, RagImageItem[]>
): QueuedInlineImage[] {
  const sorted = Object.keys(inlineImages)
    .filter((p) => /^\d+$/.test(p))
    .sort((a, b) => Number(a) - Number(b));
  const queue: QueuedInlineImage[] = [];
  for (const page of sorted) {
    for (const img of inlineImages[page] ?? []) {
      queue.push({ img, page });
    }
  }
  return queue;
}

/** Strip [p.N] and [img:N] markers from step text for display. */
export function cleanStepDisplayText(text: string): string {
  return text.replace(/\[p\.\d+\]/g, "").replace(/\[img:\d+\]\s*/g, "").trim();
}

export type StepImageBinding = { img: RagImageItem; page: string };

/**
 * Assign images to step indices when the LLM omits [p.N] / [img:N] tags.
 * Keys are indices in the full steps array (including non-numbered lines).
 */
export function computeProportionalStepImages(
  steps: ParsedStep[],
  orderedQueue: QueuedInlineImage[]
): Record<number, StepImageBinding[]> {
  const hasImgTags = steps.some((s) => s.num && /\[img:\d+\]/.test(s.text));
  const hasPageCitations = steps.some((s) => s.num && /\[p\.\d+\]/.test(s.text));
  if (hasImgTags || hasPageCitations || orderedQueue.length === 0) {
    return {};
  }

  const numberedSteps = steps.filter((s) => s.num);
  const n = numberedSteps.length;
  const m = orderedQueue.length;
  const assignments: Record<number, StepImageBinding[]> = {};

  for (let qi = 0; qi < m; qi++) {
    const targetIdx = Math.max(0, Math.min(n - 1, Math.round(((qi + 0.5) / m) * n) - 1));
    const stepOrigIdx = steps.indexOf(numberedSteps[targetIdx]);
    if (stepOrigIdx >= 0) {
      if (!assignments[stepOrigIdx]) assignments[stepOrigIdx] = [];
      assignments[stepOrigIdx].push(orderedQueue[qi]);
    }
  }
  return assignments;
}
